// گزارش خطا به دیتابیس (جدول client_errors، مایگریشن 0007).
//
// قبلاً هر خطا فقط یه توستِ «خطا - دوباره امتحان کن» می‌شد و هیچ ردی نمی‌موند، پس
// خطای روی گوشیِ کاربر عملاً غیرقابل‌ردیابی بود. حالا هر جایی که خطا می‌گیریم،
// reportError صدا زده میشه.
//
// دیدن خطاها:  select * from client_errors order by created_at desc limit 50;
import { supabase } from './supabase.ts';
import { claimBrokenImage } from './brokenImage.ts';
import { isDemoMode } from './demo.ts';
import { trace } from './trace.ts';
import { dlog } from './devlog.ts';
import { platformInfo } from '../platform.ts';

/** رشته‌ی خواناییِ خطا. شکل‌های مختلفی که ازش خطا می‌گیریم رو یکدست می‌کنه. */
export function describeError(e: unknown): string {
  if (e == null) return 'unknown error';
  if (typeof e === 'string') return e;
  if (e instanceof Error && e.message) return e.message;

  // خطاهای Supabase (PostgrestError / StorageError / AuthError) شکل‌های متفاوتی دارن
  const any = e as Record<string, unknown>;
  const parts = [any.message, any.error, any.error_description, any.hint, any.details]
    .filter((x): x is string => typeof x === 'string' && x.length > 0);
  if (any.statusCode || any.code) parts.push(`code=${any.statusCode ?? any.code}`);
  if (parts.length) return parts.join(' · ');

  try {
    return JSON.stringify(e).slice(0, 500);
  } catch {
    return String(e);
  }
}

/** فقط چیزهایی که مطمئناً JSON می‌شن، تا خودِ لاگ کردن خطا نده. */
function safeDetail(e: unknown): Record<string, unknown> {
  const any = (e ?? {}) as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const k of ['name', 'code', 'statusCode', 'status', 'hint', 'details', 'stack']) {
    const v = any[k];
    if (typeof v === 'string' || typeof v === 'number') out[k] = String(v).slice(0, 1000);
  }
  return out;
}

/**
 * خطا رو توی دیتابیس ثبت می‌کنه. عمداً هیچ‌وقت throw نمی‌کنه و منتظرش هم نمی‌مونیم:
 * شکستِ خودِ لاگ نباید کاربر رو گیر بندازه.
 */
export function reportError(
  where: string, e: unknown, extra?: Record<string, unknown>,
): void {
  const message = describeError(e);
  console.warn(`[goout:${where}]`, message, extra ?? '');
  // و اگه لاگِ روی دستگاه روشنه، همین‌جا هم دیده شه: ردیفِ توی دیتابیس بعداً
  // خونده می‌شه، ولی کسی که همین حالا جلوی گوشیه باید بتونه ببیندش.
  // (`console.warn`ِ بالا هم گرفته می‌شه، ولی این سطر خواناتره.)
  trace.log(`✘ ${where}: ${message.slice(0, 120)}`);
  // و توی لاگِ فعالیت هم، کنارِ کاری که درست انجام شده. جای یه خطا توی
  // ترتیبِ زمانیِ کارها معمولاً همون‌قدر می‌گه که خودِ متنش (lib/devlog).
  dlog('error', where.slice(0, 160), { ok: false, detail: { message, ...extra } });
  // توی دمو هیچ درخواستی نباید به بک‌اند واقعی بره - همون console.warn بالا کافیه.
  if (isDemoMode) return;

  void (async () => {
    try {
      const { data } = await supabase.auth.getUser();
      const p = platformInfo();
      await supabase.from('client_errors').insert({
        user_id: data.user?.id ?? null,
        where_at: where.slice(0, 120),
        message: message.slice(0, 2000),
        detail: { ...safeDetail(e), ...extra },
        app_version: p.appVersion,
        platform: `${p.os} ${p.osVersion}`,
      });
    } catch {
      /* اگه شبکه یا خودِ جدول در دسترس نیست، بی‌صدا رد شو */
    }
  })();
}

/**
 * عکسی که از اینترنت لود نشد — خوراکِ سربرگِ «عکس‌ها»ی ادمین (مایگریشن 0133).
 *
 * آدرسِ عکس عمداً در `message` می‌نشیند (نه توی detail) تا SQL بتونه با یه
 * `group by message` بشمردش. تصمیمِ «گزارش بشه یا نه» توی lib/brokenImage
 * است تا با npm test آزموده بشه؛ این‌جا فقط فرستادنه.
 */
let currentSession: () => string | null = () => null;
/**
 * شناسه‌ی نشستِ analytics را `lib/analytics` موقعِ لود این‌جا می‌گذارد — import
 * مستقیم از این‌جا زنجیره‌ی unit-testهایی را که report را می‌کشند می‌شکست.
 */
export const setReportSession = (fn: () => string | null): void => { currentSession = fn; };

export function reportBrokenImage(url: string | null | undefined, screen?: string): void {
  const u = claimBrokenImage(url);
  // `session`: آستانه‌ی «۵ نشستِ مختلف» در `suspect_images()` (0161) با همین
  // شمرده می‌شود — مهمان شناسه‌ی دیگری ندارد.
  if (u) reportError('image.load', u, { session: currentSession(), ...(screen ? { screen } : {}) });
}

/**
 * خطاهای جاوااسکریپتی که هیچ‌کس نگرفته (کرشِ صفحه) رو هم می‌گیره.
 * یه بار موقع بالا اومدن اپ صدا زده میشه.
 */
export function installGlobalErrorReporter(): void {
  const g = globalThis as unknown as {
    ErrorUtils?: {
      getGlobalHandler: () => (e: unknown, isFatal?: boolean) => void;
      setGlobalHandler: (fn: (e: unknown, isFatal?: boolean) => void) => void;
    };
  };
  if (!g.ErrorUtils) return;

  const previous = g.ErrorUtils.getGlobalHandler();
  g.ErrorUtils.setGlobalHandler((e, isFatal) => {
    reportError('global', e, { fatal: !!isFatal });
    previous?.(e, isFatal);   // رفتار پیش‌فرض (نمایش صفحه‌ی قرمز) حفظ شه
  });
}
